// useSpeech.js - Text-to-Speech hook using Web Speech API
import { useState, useEffect, useCallback, useRef } from 'react';

const useSpeech = () => {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const utteranceRef = useRef(null);

    // Initialize speech synthesis
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

    // Get Spanish voice if available
    const getSpanishVoice = useCallback(() => {
        if (!synth) return null;
        const voices = synth.getVoices();
        // Prefer Spanish voices
        const spanishVoice = voices.find(v => v.lang.startsWith('es')) ||
            voices.find(v => v.lang.startsWith('en')) ||
            voices[0];
        return spanishVoice;
    }, [synth]);

    // Speak text
    const speak = useCallback((text, taskId = null) => {
        if (!synth) {
            console.warn('Speech synthesis not supported');
            return;
        }

        // Stop current speech if any
        synth.cancel();

        // Clean text (remove markdown, emojis, etc)
        const cleanText = text
            .replace(/[#*_`]/g, '') // Remove markdown
            .replace(/\[.*?\]/g, '') // Remove brackets
            .replace(/https?:\/\/\S+/g, '') // Remove URLs
            .replace(/[^\w\sáéíóúñüÁÉÍÓÚÑÜ.,!?¿¡()-]/g, ' ') // Keep only text
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();

        if (!cleanText) return;

        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.voice = getSpanishVoice();
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        utterance.lang = 'es-ES';

        utterance.onstart = () => {
            setIsSpeaking(true);
            setCurrentTaskId(taskId);
        };

        utterance.onend = () => {
            setIsSpeaking(false);
            setCurrentTaskId(null);
        };

        utterance.onerror = () => {
            setIsSpeaking(false);
            setCurrentTaskId(null);
        };

        utteranceRef.current = utterance;
        synth.speak(utterance);
    }, [synth, getSpanishVoice]);

    // Stop speaking
    const stop = useCallback(() => {
        if (synth) {
            synth.cancel();
            setIsSpeaking(false);
            setCurrentTaskId(null);
        }
    }, [synth]);

    // Toggle speaking
    const toggle = useCallback((text, taskId = null) => {
        if (isSpeaking && currentTaskId === taskId) {
            stop();
        } else {
            speak(text, taskId);
        }
    }, [isSpeaking, currentTaskId, speak, stop]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (synth) {
                synth.cancel();
            }
        };
    }, [synth]);

    // Load voices (needed for some browsers)
    useEffect(() => {
        if (synth) {
            synth.getVoices();
            synth.onvoiceschanged = () => synth.getVoices();
        }
    }, [synth]);

    return {
        speak,
        stop,
        toggle,
        isSpeaking,
        currentTaskId,
        isSupported: !!synth
    };
};

export default useSpeech;
